/**
 * Toast bridge — delegates the legacy shadcn `useToast()`/`toast()` API to sonner.
 *
 * The app mounts ONLY sonner's <Toaster /> (App.tsx, styled in components/ui/sonner). The original
 * shadcn/Radix toast store this hook once fed required its own <Toaster /> viewport from
 * components/ui/toaster — which was never mounted, so every toast fired through this hook
 * rendered NOTHING (found 2026-07-11: the RightsEditModal validation toast fired invisibly
 * while the invalid field sat scrolled out of view). ~50 call sites use this hook with the
 * `{ title, description, variant }` shape and none read the return value or extra options,
 * so a thin delegate keeps every call site working with one real rendering system.
 */
import * as React from "react";

// The app toast (lib/toast): a destructive toast is an error, and errors stay until dismissed.
import { toast as sonnerToast } from "@/lib/toast";

type ToastInput = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: "default" | "destructive";
  /** Milliseconds. Omitted: 4s, or until dismissed for a destructive (error) toast. */
  duration?: number;
  /** One button on the toast, e.g. Undo. */
  action?: { label: string; onClick: () => void };
};

function toast({ title, description, variant, duration, action }: ToastInput) {
  const options = { description, duration, action };
  const id =
    variant === "destructive"
      ? sonnerToast.error(title ?? "Something went wrong", options)
      : sonnerToast(title ?? "", options);

  return {
    id: String(id),
    dismiss: () => sonnerToast.dismiss(id),
    update: (next: ToastInput) => {
      const nextOptions = { id, description: next.description, duration: next.duration };
      if (next.variant === "destructive") sonnerToast.error(next.title ?? "", nextOptions);
      else sonnerToast(next.title ?? "", nextOptions);
    },
  };
}

function useToast() {
  return {
    toast,
    dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
  };
}

export { useToast, toast };
export type { ToastInput };
